import { useState, useEffect, useCallback } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { useFileUpload } from '@/hooks/use-file-upload';
import { useUploadFileMutation } from '@/features/_shared/api/upload-file.api';
import { axiosExtractErrorMessage, AxiosErrorCustom } from '@/lib/axios';
import { FileWithPreview } from '@/features/_shared/hooks/use-file-upload';
import { TFileItem } from '@/features/_shared/types/upload-file.type';
import { TCreateTicketValidation } from '../../../api/create-ticket.api';

/**
 * Type definitions for form attachments
 */
export type FormAttachment = {
  fileName: string;
  size: number;
  uri: string;
  contentType: string;
};

/**
 * Helper function to map form attachments to TFileItem format
 */
const mapFormAttachmentToFileItem = (attachment: FormAttachment): TFileItem => ({
  filename: attachment.fileName,
  key: attachment.uri,
  url: attachment.uri,
  filesize: attachment.size,
  mimetype: attachment.contentType,
});

/**
 * Helper function to map TFileItem to form attachment format
 */
const mapFileItemToFormAttachment = (file: TFileItem): FormAttachment => ({
  fileName: file.filename,
  size: file.filesize,
  uri: file.url,
  contentType: file.mimetype,
});

interface UseCreateTicketAttachmentsProps {
  form: UseFormReturn<TCreateTicketValidation>;
}

export function useCreateTicketAttachments({ form }: UseCreateTicketAttachmentsProps) {
  // ===== Hooks =====
  const showToast = useCustomToast();

  // ===== State =====
  const [extractedAttachments, setExtractedAttachments] = useState<TFileItem[]>([]);
  const [uploadingFileIds, setUploadingFileIds] = useState<string[]>([]);

  // Watch form attachments
  const watchedAttachments = useWatch({ control: form.control, name: 'attachments' }) as FormAttachment[] | undefined;

  // ===== File Upload Hook =====
  const { clearFile } = useFileUpload({
    multiple: true,
    acceptedFileTypes: ['.p8', '.pdf', '.doc', '.docx', '.txt', '.jpg', '.png', '.gif'],
    onFileSelect: () => {
      form.clearErrors('attachments');
    },
    onError: (error) => {
      form.setError('attachments', {
        type: 'manual',
        message: error,
      });
    },
  });

  // ===== Mutations =====
  // Upload Files
  const mutationUploadFiles = useUploadFileMutation({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || 'Error upload file, please try again';
        showToast({ message: errorMessage, type: 'error' });
        setUploadingFileIds([]);
      },
      onSuccess: (data) => {
        const files = data.payload.files as TFileItem[];
        const currentAttachments = watchedAttachments || [];

        // Map uploaded files to form attachment format
        const mappedFiles = files.map(mapFileItemToFormAttachment);

        // Filter out duplicates based on URI
        const existingUris = new Set(currentAttachments.map((att) => att.uri));
        const newFiles = mappedFiles.filter((file) => !existingUris.has(file.uri));

        // Update form with new files
        if (newFiles.length > 0) {
          form.setValue('attachments', [...currentAttachments, ...newFiles]);
        }

        showToast({ message: 'Files uploaded successfully', type: 'success' });
        setUploadingFileIds([]);
      },
    },
  });

  // ===== Effects =====
  // Sync attachments from form to extracted attachments
  useEffect(() => {
    if (!watchedAttachments || watchedAttachments.length === 0) {
      setExtractedAttachments([]);
      return;
    }

    const mappedAttachments = watchedAttachments.map(mapFormAttachmentToFileItem);
    setExtractedAttachments(mappedAttachments);
  }, [watchedAttachments]);

  // ===== Handlers =====
  // Add Files
  const handleAddFiles = useCallback(
    (files: FileWithPreview[]) => {
      const filesToUpload = files.map((file) => file.file);
      const fileIds = files.map((file) => file.id);
      setUploadingFileIds(fileIds);
      mutationUploadFiles.mutate({ files: filesToUpload as File[] });
    },
    [mutationUploadFiles]
  );

  // Remove File
  const handleRemoveFile = useCallback(
    (removedFile: FileWithPreview) => {
      // Remove from uploading files if present
      setUploadingFileIds((prev) => prev.filter((id) => id !== removedFile.id));

      // Filter out the removed file from attachments
      const filteredAttachments = extractedAttachments.filter((attachment) => attachment.url !== removedFile.preview);

      // Convert back to form attachment format and update form
      const mappedAttachments = filteredAttachments.map(mapFileItemToFormAttachment);
      form.setValue('attachments', mappedAttachments);
    },
    [extractedAttachments, form]
  );

  // ===== Return =====
  return {
    extractedAttachments,
    uploadingFileIds,
    isUploadingFiles: Boolean(mutationUploadFiles?.isPending),
    handleAddFiles,
    handleRemoveFile,
    clearFile,
  };
}

export type UseCreateTicketAttachmentsReturn = ReturnType<typeof useCreateTicketAttachments>;
